/**
 * @element Modal
 * @description Canonical, accessible dialog primitive.
 *
 * The audit found this file EMPTY while 5+ dialogs declared role="dialog"/
 * aria-modal with no focus trap and no Escape-to-close — keyboard users tabbed
 * straight out into the page behind them (WCAG 2.1.2 / 2.4.3). This primitive
 * is what those dialogs should migrate onto.
 *
 * It provides:
 *   - focus trap + Escape-to-close + focus restore (via useFocusTrap)
 *   - a labelled role="dialog" aria-modal container
 *   - backdrop click-to-close (opt-out via closeOnBackdrop={false})
 *   - body scroll lock while open
 *   - portal render so stacking context / overflow never clips it
 *   - an icon close button with an accessible name
 *
 * Usage:
 *   <Modal open={open} onClose={close} title="Delete project">
 *     <p>This can't be undone.</p>
 *     <Modal.Footer>
 *       <Button variant="ghost" onClick={close}>Cancel</Button>
 *       <Button variant="danger" onClick={confirm}>Delete</Button>
 *     </Modal.Footer>
 *   </Modal>
 */
import { useEffect, useId } from 'react';
import { createPortal } from 'react-dom';
import { useFocusTrap } from '../../hooks/useFocusTrap';

const SIZES = {
  sm: 'max-w-sm',
  md: 'max-w-lg',
  lg: 'max-w-2xl',
  xl: 'max-w-4xl',
};

export function Modal({
  open,
  onClose,
  title,
  description,
  children,
  footer,
  size = 'md',
  initialFocusRef,
  closeOnBackdrop = true,
  hideClose = false,
  className = '',
}) {
  const containerRef = useFocusTrap({ active: !!open, onEscape: onClose, initialFocusRef });
  const titleId = useId();
  const descId = useId();

  // Lock background scroll while the dialog is open.
  useEffect(() => {
    if (!open) return undefined;
    const prev = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => { document.body.style.overflow = prev; };
  }, [open]);

  if (!open) return null;
  if (typeof document === 'undefined') return null;

  return createPortal(
    <div className="fixed inset-0 z-[100] flex items-center justify-center p-4">
      {/* Backdrop */}
      <div
        className="absolute inset-0 bg-black/40 backdrop-blur-[2px]"
        aria-hidden="true"
        onClick={closeOnBackdrop ? onClose : undefined}
      />

      {/* Dialog */}
      <div
        ref={containerRef}
        role="dialog"
        aria-modal="true"
        aria-labelledby={title ? titleId : undefined}
        aria-describedby={description ? descId : undefined}
        className={`relative w-full ${SIZES[size] || SIZES.md} rounded-card bg-background shadow-2xl border border-gray-100 max-h-[90vh] overflow-y-auto ${className}`}
      >
        {(title || !hideClose) && (
          <div className="flex items-start justify-between gap-4 px-6 pt-5 pb-3 border-b border-gray-100">
            <div className="min-w-0">
              {title && (
                <h2 id={titleId} className="text-lg font-semibold text-neutral truncate">
                  {title}
                </h2>
              )}
              {description && (
                <p id={descId} className="mt-1 text-sm text-gray-500">
                  {description}
                </p>
              )}
            </div>
            {!hideClose && (
              <button
                type="button"
                onClick={onClose}
                aria-label="Close dialog"
                className="shrink-0 -mr-1 rounded-full p-1.5 text-gray-400 hover:bg-gray-100 hover:text-gray-700"
              >
                <svg viewBox="0 0 24 24" className="h-5 w-5" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden="true">
                  <path strokeLinecap="round" strokeLinejoin="round" d="M6 6l12 12M18 6L6 18" />
                </svg>
              </button>
            )}
          </div>
        )}

        <div className="px-6 py-4">{children}</div>

        {footer && (
          <div className="flex items-center justify-end gap-3 px-6 py-4 border-t border-gray-100">
            {footer}
          </div>
        )}
      </div>
    </div>,
    document.body
  );
}

// Convenience footer wrapper for the common right-aligned action row.
Modal.Footer = function ModalFooter({ children }) {
  return <div className="flex items-center justify-end gap-3">{children}</div>;
};

export default Modal;
